"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import type { GlobeMethods, GlobeProps } from "react-globe.gl";
import type { Destination } from "@/lib/destinations/types";

// react-globe.gl utilise WebGL → SSR off
const Globe = dynamic(() => import("react-globe.gl"), {
  ssr: false,
  loading: () => (
    <div className="flex h-full w-full items-center justify-center text-[10px] tracking-[0.3em] uppercase text-white/30 font-body">
      Chargement…
    </div>
  ),
});

type Props = {
  destinations: Destination[];
  /** id de la destination sélectionnée — la caméra zoome dessus à chaque changement */
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Locale pour les libellés (labels au survol) */
  locale: "fr" | "en";
};

// Position caméra par défaut : zoomée sur Cotonou (golfe de Guinée)
const DEFAULT_POV = { lat: 6.3703, lng: 2.3912, altitude: 0.75 };
const FOCUS_ALTITUDE = 1.2;

export default function GlobeInteractive({
  destinations,
  selectedId,
  onSelect,
  locale,
}: Props) {
  const globeRef = useRef<GlobeMethods | undefined>(undefined);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Redimensionnement responsive
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      setSize({ w: el.clientWidth, h: el.clientHeight });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Init caméra + contrôles + relief — via onGlobeReady (fiable) :
  // un useEffect basé sur la taille se déclenchait AVANT que la ref du globe
  // (import dynamique) soit attachée → la position par défaut n'était jamais
  // appliquée et on restait sur la vue par défaut de globe.gl.
  const initGlobe = () => {
    const g = globeRef.current;
    if (!g) return;
    g.pointOfView(DEFAULT_POV, 0);
    // Auto-rotation douce
    const controls = g.controls() as {
      autoRotate: boolean;
      autoRotateSpeed: number;
      enableZoom: boolean;
    };
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.35;
    controls.enableZoom = true;

    // Léger relief topographique — assez pour révéler les chaînes montagneuses
    // sans dénaturer la lisibilité de la texture Blue Marble.
    // (globeMaterial manque dans les typings → garde-fou, effet cosmétique)
    const matFn = (
      g as unknown as {
        globeMaterial?: () => { bumpScale: number; needsUpdate: boolean };
      }
    ).globeMaterial;
    if (typeof matFn === "function") {
      const material = matFn();
      material.bumpScale = 2;
      material.needsUpdate = true;
    }
  };

  // Tween caméra quand une ville est sélectionnée
  useEffect(() => {
    const g = globeRef.current;
    if (!g || !selectedId) return;
    const dest = destinations.find((d) => d.id === selectedId);
    if (!dest) return;
    // Couper l'auto-rotation tant qu'une ville est sélectionnée
    const controls = g.controls() as { autoRotate: boolean };
    controls.autoRotate = false;
    g.pointOfView(
      { lat: dest.coords[0], lng: dest.coords[1], altitude: FOCUS_ALTITUDE },
      1400
    );
  }, [selectedId, destinations]);

  // Préparer les points pour le globe
  const points = destinations.map((d) => ({
    id: d.id,
    lat: d.coords[0],
    lng: d.coords[1],
    label: `${d.city}, ${d.country[locale]}`,
    selected: d.id === selectedId,
  }));

  type Point = (typeof points)[number];

  const globeProps: Partial<GlobeProps> = {
    onGlobeReady: initGlobe,
    width: size.w,
    height: size.h,
    backgroundColor: "rgba(0, 0, 0, 0)",
    // Blue Marble NASA : océans bleus + continents verts/bruns naturels
    globeImageUrl: "/destinations/earth-blue-marble.jpg",
    bumpImageUrl: "/destinations/earth-topology.png",
    showAtmosphere: true,
    // Ciel bleu vif (halo atmosphérique style "Terre vue de l'espace")
    atmosphereColor: "#4a8fd4",
    atmosphereAltitude: 0.28,

    pointsData: points,
    pointLat: (d) => (d as Point).lat,
    pointLng: (d) => (d as Point).lng,
    pointLabel: (d) => (d as Point).label,
    pointAltitude: (d) => ((d as Point).selected ? 0.04 : 0.012),
    pointRadius: (d) => ((d as Point).selected ? 0.55 : 0.38),
    pointColor: (d) =>
      (d as Point).selected ? "#ffffff" : "rgba(255,255,255,0.65)",
    onPointClick: (p) => {
      const point = p as Point;
      onSelect(point.id);
    },

    ringsData: points.filter((p) => p.selected),
    ringLat: (d) => (d as Point).lat,
    ringLng: (d) => (d as Point).lng,
    ringColor: () =>
      ((t: number) => `rgba(255,255,255,${1 - t})`) as unknown as string,
    ringMaxRadius: 4,
    ringPropagationSpeed: 2.5,
    ringRepeatPeriod: 1200,
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden"
      aria-label="Globe interactif des destinations Welconcept"
    >
      {size.w > 0 && size.h > 0 && <Globe ref={globeRef} {...globeProps} />}
    </div>
  );
}
